import React, { FC, useEffect, useMemo } from 'react';
import { ContentTransformTypes, ImageElement, VideoElement } from '@bynder-studio/render-core';
import { Dropdown, Form } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { IconCrop } from '@bynder/icons';
import useEditorSelectedElementContentTransform from 'packages/hooks/useEditorSelectedElementContentTransform';
import useEditorSelectedElement from 'packages/hooks/useEditorSelectedElement';
import useEditor from 'packages/hooks/useEditor';
import useForceUpdate from 'packages/hooks/useForceUpdate';
import useAccessRights from 'packages/hooks/useAccessRights';
import { getFreeCropModeParams } from 'packages/common/editor/helpers/index';
import { ActiveSplits } from 'packages/store/user/types';
import FormLabelLock from 'packages/pages/editor/RightSideMenu/FormComponents/FormLabelWithLock';
import { CropModeBaseProps, CropModeProps, TransformOptions, TransformSetting } from './types';
import { CropModeButton, CropModeListItem } from './styled';
import CropSetting from './CropSetting';
import generateTestId from 'packages/helpers/testIdHelpers';
import useCropModeShortcuts from 'packages/hooks/shortcuts/useCropModeSchortcuts';

const TRANSFORM_OPTOINS: TransformOptions = {
    [ContentTransformTypes.CONTAIN]: {
        value: ContentTransformTypes.CONTAIN,
        label: 'editor.sidebar.shots.crop.mode.contain',
        icon: <IconCrop />,
    },
    [ContentTransformTypes.COVER]: {
        value: ContentTransformTypes.COVER,
        label: 'editor.sidebar.shots.crop.mode.cover',
        icon: <IconCrop />,
    },
    [ContentTransformTypes.COVER_HEIGHT]: {
        value: ContentTransformTypes.COVER_HEIGHT,
        label: 'editor.sidebar.shots.crop.mode.cover-height',
        icon: <IconCrop />,
    },
    [ContentTransformTypes.COVER_WIDTH]: {
        value: ContentTransformTypes.COVER_WIDTH,
        label: 'editor.sidebar.shots.crop.mode.cover-width',
        icon: <IconCrop />,
    },
    [ContentTransformTypes.FREE]: {
        value: ContentTransformTypes.FREE,
        label: 'editor.sidebar.shots.crop.mode.free',
        icon: <IconCrop />,
    },
};

const testIdDropdown = generateTestId('shots_crop_mode__dropdown');
const testIdScale = generateTestId('shots_crop_mode__scale');
const testIdHorizontalOffset = generateTestId('shots_crop_mode__horizontal_offset');
const testIdVerticalOffset = generateTestId('shots_crop_mode__vertical_offset');
const testIdHorizontalAlignment = generateTestId('shots_crop_mode__horizontal_alignment');
const testIdVerticalAlignment = generateTestId('shots_crop_mode__vertical_alignment');
const AUTO_SCALE_MODES = new Set([
    ContentTransformTypes.COVER,
    ContentTransformTypes.CONTAIN,
    ContentTransformTypes.COVER_WIDTH,
    ContentTransformTypes.COVER_HEIGHT,
]);

const CropModeBase: FC<CropModeBaseProps> = ({
    transformType,
    setTransformType,
    translate,
    setSettingValue,
    horizontalAlignment,
    verticalAlignment,
    horizontalOffset,
    minHorizontalOffset,
    maxHorizontalOffset,
    verticalOffset,
    minVerticalOffset,
    maxVerticalOffset,
    scale,
    minScale,
    disabled,
    locked,
    toggleLock,
    lockFeatureEnabled,
}) => {
    const selectedOption = TRANSFORM_OPTOINS[transformType];
    const isFreeform = selectedOption.value === ContentTransformTypes.FREE;
    const isCoverMode = selectedOption.value === ContentTransformTypes.COVER;
    const isCoverHeight = selectedOption.value === ContentTransformTypes.COVER_HEIGHT;
    const isCoverWidth = selectedOption.value === ContentTransformTypes.COVER_WIDTH;
    const isModeSwitchDisabled = disabled || locked;

    useCropModeShortcuts({
        toggleMode: () => {
            if (!isModeSwitchDisabled) {
                if (selectedOption.value === ContentTransformTypes.CONTAIN) {
                    setTransformType(ContentTransformTypes.COVER);
                } else {
                    setTransformType(ContentTransformTypes.CONTAIN);
                }
            }
        },
    });

    const options = Object.values(TRANSFORM_OPTOINS).map(({ label, ...props }) => (
        <Dropdown.Item
            key={label}
            onClick={() => !isModeSwitchDisabled && setTransformType(ContentTransformTypes[props.value])}
            isChecked={selectedOption.label === label}
            isDisabled={isModeSwitchDisabled}
        >
            {translate(label)}
        </Dropdown.Item>
    ));

    const defaultCropSettings = (
        <>
            {!isCoverWidth && (
                <CropSetting
                    label={translate('editor.sidebar.shots.crop.horizontal-align')}
                    name="horizontalAlignment"
                    format="percent"
                    min={-1}
                    max={1}
                    value={horizontalAlignment}
                    setSettingValue={setSettingValue}
                    disabled={disabled}
                    {...testIdHorizontalAlignment}
                />
            )}
            {!isCoverHeight && (
                <CropSetting
                    label={translate('editor.sidebar.shots.crop.vertical-align')}
                    name="verticalAlignment"
                    format="percent"
                    min={-1}
                    max={1}
                    value={verticalAlignment}
                    setSettingValue={setSettingValue}
                    disabled={disabled}
                    {...testIdVerticalAlignment}
                />
            )}
        </>
    );

    const coverModeOptions = (
        <CropSetting
            label={translate('editor.sidebar.shots.crop.scale')}
            name="scale"
            format="percent"
            min={minScale}
            max={2}
            value={scale}
            isFromCenter={false}
            setSettingValue={setSettingValue}
            disabled={disabled || scale > 2}
            {...testIdScale}
        />
    );
    const freeformModeOptions = (
        <>
            <CropSetting
                label={translate('editor.sidebar.shots.crop.scale')}
                name="scale"
                format="percent"
                min={0}
                max={2}
                value={scale}
                isFromCenter={false}
                setSettingValue={setSettingValue}
                disabled={disabled}
                {...testIdScale}
            />
            <CropSetting
                label={translate('editor.sidebar.shots.crop.horizontal-offset')}
                name="horizontalOffset"
                format="pixels"
                min={minHorizontalOffset}
                max={maxHorizontalOffset}
                value={horizontalOffset}
                setSettingValue={setSettingValue}
                disabled={disabled}
                {...testIdHorizontalOffset}
            />
            <CropSetting
                label={translate('editor.sidebar.shots.crop.vertical-offset')}
                name="verticalOffset"
                format="pixels"
                min={minVerticalOffset}
                max={maxVerticalOffset}
                value={verticalOffset}
                setSettingValue={setSettingValue}
                disabled={disabled}
                {...testIdVerticalOffset}
            />
        </>
    );

    return (
        <>
            <CropModeListItem>
                <Form.Group>
                    {lockFeatureEnabled ? (
                        <FormLabelLock
                            testId="shots_crop_mode"
                            label={translate('editor.sidebar.shots.crop.label')}
                            locked={locked}
                            toggleLock={toggleLock}
                            htmlFor="shots_crop_mode"
                            isDisabled={disabled}
                        />
                    ) : (
                        <Form.Label>{translate('editor.sidebar.shots.crop.label')}</Form.Label>
                    )}
                    <Dropdown
                        trigger={({ isOpen, onClick, ...triggerProps }) => (
                            <CropModeButton
                                isPressed={isOpen}
                                rightIcon={<Dropdown.Arrow />}
                                isFullWidth
                                isDisabled={isModeSwitchDisabled}
                                onClick={!isModeSwitchDisabled ? onClick : () => {}}
                                {...triggerProps}
                                {...testIdDropdown}
                            >
                                {translate(selectedOption.label)}
                            </CropModeButton>
                        )}
                        position="bottom"
                    >
                        {options}
                    </Dropdown>
                </Form.Group>
            </CropModeListItem>
            {isCoverMode && coverModeOptions}
            {isFreeform && freeformModeOptions}
            {!isFreeform && defaultCropSettings}
        </>
    );
};

const CropMode: FC<CropModeProps> = ({ disabled }) => {
    const { translate } = useTranslate();

    const { creativeModel } = useEditor();
    const { selectedElement } = useEditorSelectedElement<ImageElement | VideoElement>();
    const { isSplitEnabled } = useAccessRights();
    const lockFeatureEnabled = isSplitEnabled(ActiveSplits.studioImageCropModeLock);

    // todo: types, error handling
    const { contentTransform } = useEditorSelectedElementContentTransform();
    const { id, locked } = selectedElement;

    // Force re-render when element dimensions change so all dimension-dependent
    // values (minScale, currentScale, offset bounds) are recalculated.
    const forceUpdate = useForceUpdate();

    useEffect(() => {
        if (!creativeModel) {
            return;
        }

        return creativeModel.onElementUpdate(id, forceUpdate);
    }, [creativeModel, id, forceUpdate]);

    // Read fresh values from selectedElement on every render (it's a mutable object)
    const { dimension } = selectedElement;
    const { width: naturalWidth, height: naturalHeight } = selectedElement.naturalDimension;
    const coverScale = Math.max(dimension.width / naturalWidth, dimension.height / naturalHeight);
    const containScale = Math.min(dimension.width / naturalWidth, dimension.height / naturalHeight);
    const coverWidthScale = dimension.width / naturalWidth;
    const coverHeightScale = dimension.height / naturalHeight;

    const getModeDerivedScale = (mode: ContentTransformTypes) => {
        if (mode === ContentTransformTypes.COVER) {
            return coverScale;
        }

        if (mode === ContentTransformTypes.CONTAIN) {
            return containScale;
        }

        if (mode === ContentTransformTypes.COVER_WIDTH) {
            return coverWidthScale;
        }

        if (mode === ContentTransformTypes.COVER_HEIGHT) {
            return coverHeightScale;
        }
    };

    const isFreeMode = (mode: ContentTransformTypes) => mode === ContentTransformTypes.FREE;
    const isAutoScaleMode = (mode: ContentTransformTypes) => AUTO_SCALE_MODES.has(mode);

    const hasValidManualScale = (transform = contentTransform) => {
        if (isFreeMode(transform.type)) {
            return transform.scale !== null && transform.scale !== undefined;
        }

        if ('hasManualScale' in transform) {
            return transform.hasManualScale();
        }

        return typeof transform.scale === 'number' && transform.scale > 0;
    };

    const cropModeLocked = lockFeatureEnabled ? (contentTransform?.locked ?? false) : false;

    const toggleCropModeLock = () => {
        const { scale, ...restTransform } = contentTransform;

        creativeModel.updateElement(id, {
            contentTransform: {
                ...restTransform,
                ...(hasValidManualScale() ? { scale } : {}),
                locked: !cropModeLocked,
            },
        });
    };

    const setTransformType = (newValue: ContentTransformTypes) => {
        const { scale, ...restTransform } = contentTransform;
        const keepScale = isFreeMode(newValue);
        const modeScale = getModeDerivedScale(newValue);
        const params = {
            contentTransform: {
                ...restTransform,
                ...(modeScale !== null && modeScale !== undefined ? { scale: modeScale } : {}),
                ...(keepScale && scale !== null && scale !== undefined ? { scale } : {}),
                ...(isFreeMode(newValue) ? getFreeCropModeParams(selectedElement) : {}),
                type: newValue,
            },
        };

        creativeModel.updateElement(id, params);
    };

    const setSettingValue = (inputValue: number, setting: TransformSetting) => {
        const newTransform = { ...contentTransform };
        const { scale, horizontalOffset = 0, verticalOffset = 0 } = contentTransform;
        let newValue = inputValue;

        switch (setting) {
            case 'horizontalAlignment':
            case 'verticalAlignment':
                newTransform[setting] = newValue;

                if (!isFreeMode(contentTransform.type)) {
                    const hasManualScaleInCurrentMode = hasValidManualScale(contentTransform);

                    newTransform.scale = hasManualScaleInCurrentMode
                        ? currentScale
                        : (getModeDerivedScale(contentTransform.type) ?? currentScale);
                }

                break;
            case 'horizontalOffset':
            case 'verticalOffset':
                newTransform[setting] = newValue;
                break;
            case 'scale':
                // Enforce minimum scale for COVER mode - can't go below initial cover scale
                if (contentTransform.type === ContentTransformTypes.COVER) {
                    if (newValue < coverScale) {
                        newValue = coverScale;
                    }
                }

                // For COVER/CONTAIN modes, offsets are calculated from alignment by render-core
                // So we only set the scale and let calculateBoxCrop handle positioning
                if (isAutoScaleMode(contentTransform.type)) {
                    newTransform.scale = newValue;
                } else {
                    // For FREE mode, scale from current position
                    // Use the actual current scale value
                    const currentScale = scale;
                    newTransform.horizontalOffset = Math.round(
                        horizontalOffset + (naturalWidth * currentScale - naturalWidth * newValue) / 2,
                    );
                    newTransform.verticalOffset = Math.round(
                        verticalOffset + (naturalHeight * currentScale - naturalHeight * newValue) / 2,
                    );
                    newTransform.scale = newValue;
                }

                break;
            default:
        }

        creativeModel.updateElement(id, { contentTransform: newTransform });
    };

    const minScale = useMemo(() => {
        return getModeDerivedScale(contentTransform.type) ?? 0;
    }, [contentTransform.type, coverScale, containScale, coverWidthScale, coverHeightScale]);

    // When element is resized, persist scale adjustments for auto-scale modes
    // so the canvas keeps the expected crop behavior.
    useEffect(() => {
        if (!creativeModel || id === null || id === undefined) {
            return;
        }

        // Read directly from the mutable selectedElement for the freshest value
        const ct = selectedElement.contentTransform;

        if (isFreeMode(ct.type)) {
            return;
        }

        const modeDerivedScale = getModeDerivedScale(ct.type);

        if (modeDerivedScale === null || modeDerivedScale === undefined) {
            return;
        }

        if (ct.type !== ContentTransformTypes.COVER) {
            if (ct.scale !== modeDerivedScale) {
                creativeModel.updateElement(id, {
                    contentTransform: { ...ct, scale: modeDerivedScale },
                });
            }

            return;
        }

        // Only clamp if the user has explicitly set a scale value.
        // When scale is null/undefined, render-core calculates cover scale internally.
        if (ct.scale !== null && ct.scale !== undefined) {
            const maxScale = 2;

            if (ct.scale < minScale && minScale <= maxScale) {
                // Scale is below minimum and minScale is within allowed range - clamp up
                creativeModel.updateElement(id, {
                    contentTransform: {
                        ...ct,
                        scale: minScale,
                    },
                });
            }
            // When minScale > maxScale (box is so large that cover requires > 200%),
            // don't persist the inflated scale. calculateBoxCrop already uses
            // Math.max(manualScale, autoScale) so the image covers the box at runtime.
        }
    }, [creativeModel, id, selectedElement, minScale, coverScale, containScale, coverWidthScale, coverHeightScale]);

    // Calculate current scale properly based on mode
    const hasManualScale = hasValidManualScale(contentTransform);

    let currentScale = hasManualScale
        ? (contentTransform.scale ?? 1)
        : (getModeDerivedScale(contentTransform.type) ?? 1);

    if (currentScale < minScale) {
        currentScale = minScale;
    }

    return (
        <CropModeBase
            transformType={contentTransform.type}
            setTransformType={setTransformType}
            verticalAlignment={contentTransform?.verticalAlignment ?? 0.5}
            horizontalAlignment={contentTransform?.horizontalAlignment ?? 0.5}
            scale={currentScale}
            minScale={minScale}
            verticalOffset={contentTransform?.verticalOffset ?? 0}
            minVerticalOffset={Math.floor(-1 * currentScale * naturalHeight)}
            maxVerticalOffset={Math.ceil(dimension.height)}
            horizontalOffset={contentTransform?.horizontalOffset ?? 0}
            minHorizontalOffset={Math.floor(-1 * currentScale * naturalWidth)}
            maxHorizontalOffset={Math.ceil(dimension.width)}
            setSettingValue={setSettingValue}
            disabled={locked || disabled}
            locked={cropModeLocked}
            toggleLock={toggleCropModeLock}
            lockFeatureEnabled={lockFeatureEnabled}
            translate={translate}
        />
    );
};

export default CropMode;
